/** ***********
*
* <kpi-filter.component.ts v1.0>
*
* Copyright (c) 2023 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*
* All rights reserved.
**************/

import {  Zone } from './../../../models/zone';
import { Component, OnDestroy, OnInit, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Validators, FormGroup, FormControl, FormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subscription, combineLatest, iif, of } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { KpiService } from 'src/app/services/kpi.service';
import { selectFamilyArray$, selectFamilyLoaded$ } from '../../../store/selectors/family.selectors';
import { SnackbarComponent } from '../../shared/snackbar/snackbar';
import { TranslateService } from '@ngx-translate/core';
import { selectAssetLoaded$, selectAssetsByFamiliesIds } from 'src/app/store/selectors/asset.selectors';
import { selectZoneState$, selectZonesLoaded$ } from '../../../store/selectors/zone.selector';
import { KpiDataFormat } from 'src/app/models/kpiform';
import { Family } from 'src/app/models/family';
import { Asset } from 'src/app/models/asset';
import { ReducedZone, ZoneService } from 'src/app/services/zone.service';
import { detailExpand } from '../../shared/animations';

@Component({
    selector: 'app-kpi-filter',
    templateUrl: './kpi-filter.component.html',
    styleUrls: ['./kpi-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        detailExpand,
    ],
    standalone: false
})
export class KpiFilterComponent implements OnInit, OnDestroy {
    @Output() showOrHideFilter = new EventEmitter<boolean>();

    public displayFiltersPanel = true;

    // Main form bound to the form fields
    public filterForm: FormGroup<{
        family: FormControl<Family['id']>;
        assets: FormControl<Asset['id'][]>;
        zone: FormControl<Zone['id']>;
        inOutzone: FormControl<Zone['id']>;
        passzone: FormControl<Zone['id']>;
        startDate: FormControl<Date>;
        endDate: FormControl<Date>;
    }> = this._fb.group({
        family: this._fb.control(null, [Validators.required]),
        assets: this._fb.control([]),
        zone: this._fb.control(null, [Validators.required]),
        passzone: this._fb.control(null, [Validators.required]),
        inOutzone: this._fb.control(null),
        startDate: this._fb.control(null, [Validators.required]),
        endDate: this._fb.control(null, [Validators.required]),
    });

    // Form used for list filtering on the selects
    public searchForm: FormGroup<{
        family: FormControl<string>;
        asset: FormControl<string>;
        zone: FormControl<string>;
        inOutzone: FormControl<string>;
        passZone: FormControl<string>;
    }> = this._fb.group({
        family: this._fb.control(''),
        asset: this._fb.control(''),
        zone: this._fb.control(''),
        inOutzone: this._fb.control(''),
        passZone: this._fb.control(''),
    });

    // Loading states
    public loadings = {
        families$: this.store.select(selectFamilyLoaded$).pipe(
            map(loaded => !loaded),
        ),
        zones$: this.store.select(selectZonesLoaded$).pipe(
            map(loaded => !loaded),
        ),
        assets$: this.store.select(selectAssetLoaded$).pipe(
            map(loaded => !loaded),
        ),
    };

    public families$: Observable<Family[]> = this.store.select(selectFamilyArray$).pipe(
        tap(() => this._cd.markForCheck()),
    );

    public zones$: Observable<ReducedZone[]> = this.store.select(selectZoneState$).pipe(
        filter(state => state.loaded),
        map(zones => this._zoneService.reduceZoneWithLevels(zones.data)),
        tap(() => this._cd.markForCheck()),
    );

    public assets$: Observable<Asset[]> = this.filterForm.controls.family.valueChanges.pipe(
        startWith(null),
        distinctUntilChanged(),
        switchMap(family => iif(
            () => !!family,
            this.store.select(selectAssetsByFamiliesIds([family])),
            of([]),
        )),
        tap((assets) => {
            if (this.filterForm.value.assets?.length) {
                this.filterForm.patchValue({
                    assets: this.filterForm.value.assets.filter(
                        id => assets.find(asset => asset.id === id)
                    )
                });
            }
            this._cd.markForCheck();
        }),
    );

    private _subscription: Subscription = new Subscription();

    constructor(
        private readonly store: Store,
        private readonly _kpiService: KpiService,
        private readonly _translate: TranslateService,
        private readonly _snackBar: SnackbarComponent,
        private readonly _cd: ChangeDetectorRef,
        private readonly _zoneService: ZoneService,
        private readonly _fb: FormBuilder,
    ) {
    }

    ngOnInit() {
        this._subscription.add(
            this.filterForm.controls.family.valueChanges.pipe(
                startWith(null),
                distinctUntilChanged(),
            ).subscribe(
                family => {
                    this.filterForm.controls.assets[family ? 'enable' : 'disable']();
                }
            )
        );
        this._subscription.add(
            this._kpiService.computing$.pipe(
                distinctUntilChanged(),
                filter(v => !!v),
            ).subscribe(
                () => this._updateFilterFromKpiSelection(),
            )
        );
    }

    private _updateFilterFromKpiSelection() {
        this._subscription.add(
            combineLatest([
                this._kpiService.kpiSelection$.pipe(
                    withLatestFrom(this._kpiService.computing$),
                    filter(([data, computing]) => !computing && !!data?.family_id),
                    map(([data]) => data), // Extract only the `data` after filtering
                    takeUntil(this.filterForm.valueChanges),
                ),
                this.families$,
            ]).subscribe(
                async ([data, families]) => {
                    const familyFound = families.find(
                        ({ id }) => id === data.family_id
                    );
                    this.filterForm.patchValue({
                        family: familyFound ? data.family_id : null,
                        startDate: new Date(data.startDate),
                        endDate: new Date(data.endDate),
                        zone: data.startZone,
                        passzone: JSON.parse(data.zones)?.[0],
                        inOutzone: data.inOutZone,
                        assets: familyFound ? JSON.parse(data.assets) : [],
                    });
                }
            )
        );
    }

    /**
     * Reset the form
     */
    public clearForm() {
        this.filterForm.reset();
    }

    /**
     * Validate filter form
     * @param formValues Form raw values
     */
    public requestKpi(): void {
        const { value } = this.filterForm;
        const kpiInfo: KpiDataFormat = {
            family_id: value.family,
            assets: JSON.stringify(value.assets ?? []),
            startZone: value.zone,
            zones: JSON.stringify([value.passzone]),
            inOutZone: value.inOutzone ?? "",
            startDate: value.startDate.toISOString(),
            endDate: value.endDate.toISOString(),
        };
        this._kpiService.postKpiMain(kpiInfo).pipe(
            tap(() => {
                const text = this._translate.instant('KPI_REQUEST_SEND');
                this._snackBar.open(text, 'green-snackbar', 5000);
            }),
            // If success, check fetch the kpi data
            switchMap(() => this._kpiService.checkForAchievement()),
        ).subscribe();
    }

    /**
     * On click on display icon button, display or hide the filters panel
     */
    public displayOrHideFiltersPanel() {
        this.displayFiltersPanel = !this.displayFiltersPanel;
        this.showOrHideFilter.emit(this.displayFiltersPanel);
    }

    ngOnDestroy() {
        this._subscription.unsubscribe();
    }
}
